
import React, { useMemo, memo } from 'react'
import { Box, Typography, TextField, MenuItem, Grid, useTheme } from '@mui/material'
import SingleUserSelect from '../Forms/SingleUserSelect'
import DateRangePickerComponent from '../Forms/DateRangePickerComponent'
import { renderColorByString } from '../DataGrid/renderColorByString'
import { MtbOptimization, Task, User } from 'types'

interface RenderMonthlyTaskListProps {
  orgSK?: string
  optimizations: MtbOptimization[]
  assignees: User[]
  assigner?: object | null
  monthlyTasks: Task[]
  expanded: boolean
  onMonthlyTaskChange?: (task: Task, updates: Partial<Task>) => void
  onUpdateStatus?: (task: Task, newStatus: string, allTasksForOrg: Task[]) => void
  onUpdateMonthlyTaskProgress?: (tasks: Task[]) => void
  onUpdateActiveAssignee: (monthlyTask: Task, newActiveAssignee: User) => void
  setNotifyUsers?: React.Dispatch<React.SetStateAction<any[]>>
}

const STATUS_OPTIONS = [
  'Not Started',
  'In Progress',
  'On Hold',
  'Needs Review',
  'Need More Info',
  'Revise',
  'Done',
] as const

const RenderMonthlyTaskList: React.FC<RenderMonthlyTaskListProps> = ({
  optimizations,
  assignees,
  monthlyTasks,
  onMonthlyTaskChange,
  onUpdateStatus,
  expanded,
  onUpdateActiveAssignee,
}) => {
  const theme = useTheme()

  // Precompute a stable "last‐of‐month" endDate for sorting - memoized to avoid recalc on every render
  const sortedMonthlyTasks = useMemo(() => {
    const augmented = monthlyTasks.map(task => {
      const start = task.startDate ? new Date(task.startDate) : null
      let endOfMonth: Date | null = null

      if (start) {
        endOfMonth = new Date(start.getFullYear(), start.getMonth() + 1, 0)
      }

      return {
        ...task,
        _computedEnd: endOfMonth?.getTime() ?? 0,
        _endDateObj: endOfMonth,
      } as Task & { _computedEnd: number; _endDateObj: Date | null }
    })

    // Sort by that computed end‐of‐month
    return [...augmented].sort((a, b) => a._computedEnd - b._computedEnd)
  }, [monthlyTasks])

  // Memoize filtered optimizations to avoid recalculating on every render
  const { digitalOptimizations, seoOptimizations, insightOptimization } = useMemo(() => ({
    digitalOptimizations: optimizations?.filter(
      opt => opt?.type?.includes('digital') && !opt?.name?.includes('Insights')
    ) ?? [],
    seoOptimizations: optimizations?.filter(opt => opt?.type?.includes('seo')) ?? [],
    insightOptimization: optimizations?.filter(opt => opt?.name?.includes('Insights')) ?? [],
  }), [optimizations])

  // Static arrays - defined outside useMemo since they never change
  const dmsOptTasks = ['Observation/Optimization Made', 'Insights for AM Reporting Call']
  const seoOptTasks = ['Opts Performed']
  return (
    <Box
      sx={{
        height: '100%',
        maxHeight: expanded ? 'calc(100vh - 325px)' : 'calc(100vh - 250px)',
        overflowY: 'auto',
        display: 'flex',
        justifyContent: 'center',
        p: 1,
      }}
    >
      <Box sx={{ width: '100%', p: 1 }}>
        <fieldset style={{ border: 'none', padding: 0, margin: 0 }} disabled={false}>
          {sortedMonthlyTasks.length > 0 ? (
            sortedMonthlyTasks.map(monthlyTask => {
              const start = monthlyTask.startDate ? new Date(monthlyTask.startDate) : null
              const endOfMonth = monthlyTask._endDateObj

              return (
                <React.Fragment key={monthlyTask.SK}>
                  <Grid container wrap="nowrap" sx={{ alignItems: 'center', width: '100%' }}>
                    <Grid item xs={1} py={0.5} />
                    <Grid item xs={3} py={0.5}>
                      {monthlyTask?.isSeoMonthlyTask && seoOptTasks?.includes(monthlyTask?.name) ? (
                        <TextField
                          select
                          label={monthlyTask.name || monthlyTask.label}
                          value={monthlyTask.masterStatus || 'Not Started'}
                          onChange={e => onUpdateStatus(monthlyTask, e.target.value, monthlyTasks)}
                          size="small"
                          fullWidth
                          disabled={!seoOptimizations?.length}
                          sx={{
                            '& .MuiInputBase-root': {
                              height: 45,
                            },
                            '& .MuiInputLabel-root': {
                              color: theme.palette.text.warning, // or a specific color like '#fff'
                            },
                          }}
                        >
                          {STATUS_OPTIONS.map(option => (
                            <MenuItem
                              key={option}
                              value={option}
                              disabled={['Needs Review', 'Revise'].includes(option)}
                            >
                              <Typography sx={{ color: renderColorByString(option) }}>{option}</Typography>
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : monthlyTask?.isSeoMonthlyTask && !seoOptTasks?.includes(monthlyTask?.name) ? (
                        <TextField
                          select
                          label={monthlyTask.name || monthlyTask.label}
                          value={monthlyTask.masterStatus || 'Not Started'}
                          onChange={e => onUpdateStatus(monthlyTask, e.target.value, monthlyTasks)}
                          size="small"
                          fullWidth
                          sx={{
                            '& .MuiInputBase-root': {
                              height: 45,
                            },
                            '& .MuiInputLabel-root': {
                              color: theme.palette.text.warning, // or a specific color like '#fff'
                            },
                          }}
                        >
                          {STATUS_OPTIONS.map(option => (
                            <MenuItem
                              key={option}
                              value={option}
                              disabled={['Needs Review', 'Revise'].includes(option)}
                            >
                              <Typography sx={{ color: renderColorByString(option) }}>{option}</Typography>
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : null}
                      {monthlyTask?.isDmsMonthlyTask && monthlyTask?.name?.includes('Observation/Optimization Made') ? (
                        <TextField
                          select
                          label={monthlyTask.name || monthlyTask.label}
                          value={monthlyTask.masterStatus || 'Not Started'}
                          onChange={e => onUpdateStatus(monthlyTask, e.target.value, monthlyTasks)}
                          size="small"
                          fullWidth
                          disabled={!digitalOptimizations?.length}
                          sx={{
                            '& .MuiInputBase-root': {
                              height: 45,
                            },
                            '& .MuiInputLabel-root': {
                              color: theme.palette.text.warning, // or a specific color like '#fff'
                            },
                          }}
                        >
                          {STATUS_OPTIONS.map(option => (
                            <MenuItem
                              key={option}
                              value={option}
                              disabled={['Needs Review', 'Revise'].includes(option)}
                            >
                              <Typography sx={{ color: renderColorByString(option) }}>{option}</Typography>
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : monthlyTask?.isDmsMonthlyTask &&
                        monthlyTask?.name?.includes('Insights for AM Reporting Call') ? (
                        <TextField
                          select
                          label={monthlyTask.name || monthlyTask.label}
                          value={monthlyTask.masterStatus || 'Not Started'}
                          onChange={e => onUpdateStatus(monthlyTask, e.target.value, monthlyTasks)}
                          size="small"
                          fullWidth
                          disabled={!insightOptimization?.length}
                          sx={{
                            '& .MuiInputBase-root': {
                              height: 45,
                            },
                            '& .MuiInputLabel-root': {
                              color: theme.palette.text.warning, // or a specific color like '#fff'
                            },
                          }}
                        >
                          {STATUS_OPTIONS.map(option => (
                            <MenuItem
                              key={option}
                              value={option}
                              disabled={['Needs Review', 'Revise'].includes(option)}
                            >
                              <Typography sx={{ color: renderColorByString(option) }}>{option}</Typography>
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : monthlyTask?.isDmsMonthlyTask && !dmsOptTasks?.includes(monthlyTask?.name) ? (
                        <TextField
                          select
                          label={monthlyTask.name || monthlyTask.label}
                          value={monthlyTask.masterStatus || 'Not Started'}
                          onChange={e => onUpdateStatus(monthlyTask, e.target.value, monthlyTasks)}
                          size="small"
                          fullWidth
                          sx={{
                            '& .MuiInputBase-root': {
                              height: 45,
                            },
                            '& .MuiInputLabel-root': {
                              color: theme.palette.text.warning, // or a specific color like '#fff'
                            },
                          }}
                        >
                          {STATUS_OPTIONS.map(option => (
                            <MenuItem
                              key={option}
                              value={option}
                              disabled={['Needs Review', 'Revise'].includes(option)}
                            >
                              <Typography sx={{ color: renderColorByString(option) }}>{option}</Typography>
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : null}
                      {monthlyTask?.isAmMonthlyTask && (
                        <TextField
                          select
                          label={monthlyTask.name || monthlyTask.label}
                          value={monthlyTask.masterStatus || 'Not Started'}
                          onChange={e => onUpdateStatus(monthlyTask, e.target.value, monthlyTasks)}
                          size="small"
                          fullWidth
                          sx={{
                            '& .MuiInputBase-root': {
                              height: 45,
                            },
                            '& .MuiInputLabel-root': {
                              color: theme.palette.text.warning, // or a specific color like '#fff'
                            },
                          }}
                        >
                          {STATUS_OPTIONS.map(option => (
                            <MenuItem
                              key={option}
                              value={option}
                              disabled={['Needs Review', 'Revise'].includes(option)}
                            >
                              <Typography sx={{ color: renderColorByString(option) }}>{option}</Typography>
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </Grid>

                    <Grid item xs={3} py={0.5}>
                      <SingleUserSelect
                        name="queue"
                        value={monthlyTask.queue || null}
                        options={assignees}
                        displayLabel="Active Assignee"
                        disabled={false}
                        onChange={selected => {
                          onMonthlyTaskChange(monthlyTask, { queue: selected })
                          onUpdateActiveAssignee(monthlyTask, selected)
                        }}
                        border={''}
                      />
                    </Grid>

                    <Grid item xs={4} py={0.5}>
                      <DateRangePickerComponent
                        value={[start?.toISOString() ?? null, endOfMonth?.toISOString() ?? null]}
                        onChange={([newStart, newEnd]) =>
                          onMonthlyTaskChange(monthlyTask, { startDate: newStart, endDate: newEnd })
                        }
                        disabled={true}
                      />
                    </Grid>

                    <Grid item xs={1} />
                  </Grid>
                </React.Fragment>
              )
            })
          ) : (
            <Typography variant="body2" sx={{ p: 2, color: 'gray' }}>
              No monthly tasks available for this task.
            </Typography>
          )}
        </fieldset>
      </Box>
    </Box>
  )
}

export default memo(RenderMonthlyTaskList)
